import React, { Fragment, useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isEqual from 'lodash/isEqual';
import styled from 'styled-components';
import { Alert, LayoutSidebar, SectionHeader, TipCalcTipDataSidebar } from 'components';
import { theme, toRem } from 'design';
import { MPContent } from 'screens/MakePayment/styles';
import { setPOSResults, updateCalculatorPaymentset } from 'store/TipCalculator/actions';
import { selectCalculatorChangeLog, selectCalculatorPaymentSet, selectCalculatorPOSResults } from 'store/TipCalculator/selectors';
import { DEFAULT_POOL_UNKNOWN_JOB_TITLE, ICalculatorRule, ICalculatorRules, IPOSSegmentTotals, ISite, ITipCalculatorChangeLogEntry, SourcePaymentType, SourceType } from 'types';
import { capitalize } from 'utils/strings';
import {
  buildTipDataChangeLogEntries,
  calculateCreditCardFee,
  calculateTipCalculatorTipDataTotals,
  formatTipDataJobTitle,
  getOwnerRule,
  getOwnerRuleProviderIds,
  getRuleEligibleJobTitles,
  getSortedUniqueSegments,
  isTipDataSegmentEntryEligible,
  isVirtualRuleContributor,
  ITipCalculatorTipDataTotals,
} from 'utils/tipCalculator';
import { TipCalculatorProcessingModal } from '../components/modals/TipCalculatorProcessingModal';
import { TipCalculatorProcessingModalMode } from '../components/modals/TipCalculatorProcessingModal/types';
import AccordionListItem from '../components/TipData/AccordionListItem/AccordionListItem';
import { IEmployeeListItemProps } from '../components/TipData/EmployeeListItem/EmployeeListItem';

dayjs.extend(advancedFormat);

interface ITipDataProps {
  currentCalculatorRules: ICalculatorRules;
  site: ISite;
  onBack: () => void;
  pageHeaderContent: React.ReactNode;
}

const ListItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  :not(:last-child) {
    margin-bottom: ${toRem(theme.space.lg)};
  }
`;

const TipData = ({ currentCalculatorRules, site, onBack, pageHeaderContent }: ITipDataProps) => {
  const dispatch = useDispatch();
  const { tipCalculatorPercentFee: currentSiteTipCalcPercentFee } = site?.settings || {};

  // POS Data
  const posResults = useSelector(selectCalculatorPOSResults());

  // Change log entries
  const changeLogEntries = useSelector(selectCalculatorChangeLog());
  const [currentChangeLogEntries, setCurrentChangeLogEntries] = useState<ITipCalculatorChangeLogEntry[]>(changeLogEntries);

  // Payment Set State
  const paymentSet = useSelector(selectCalculatorPaymentSet());
  const paymentDate = paymentSet?.date;

  // get the calculator rules where there is a share policy and build array of jobs with a share policy
  const rulesWithSharePolicy: ICalculatorRule[] = currentCalculatorRules.rules.filter((rule) => rule.sharePolicy);

  /// Build jobs array for share policy rules
  const sharePolicyJobs: { title: string; default: boolean }[] = [];
  rulesWithSharePolicy.forEach((policy) => {
    if (policy.contributor) sharePolicyJobs.push({ title: policy.contributor.name.toLowerCase(), default: policy.default });
    policy.additionalContributors?.forEach((additionalJob) => sharePolicyJobs.push({ title: additionalJob.name.toLowerCase(), default: policy.default })); // add all additional jobs
    if (policy.includeNoLabor === true) sharePolicyJobs.push({ title: DEFAULT_POOL_UNKNOWN_JOB_TITLE, default: policy.default }); // add `unknown` job to handle includeNoLabor
  });

  const ruleEligibleJobTitles = useMemo(() => getRuleEligibleJobTitles(currentCalculatorRules.rules), [currentCalculatorRules.rules]);

  /**
   * this clone is used throughout the component to store any
   * changes made and will be compared to the original to determine
   * if the user has made any changes
   */
  const [paymentSetClone, setPaymentSetClone] = useState(structuredClone(paymentSet));
  const [integrationDataClone, setIntegrationDataClone] = useState(structuredClone(posResults));
  const segments = useMemo(() => {
    const uniqueSegments = getSortedUniqueSegments(integrationDataClone, paymentSetClone.segments);
    return uniqueSegments.map((segment) => {
      const segmentDetails = paymentSetClone.segments.find((s) => s.id === segment.id);
      return { ...segment, ...segmentDetails };
    });
  }, [integrationDataClone, paymentSetClone]);

  const dirty = !isEqual(paymentSet, paymentSetClone) || !isEqual(posResults, integrationDataClone);

  const calculateUpdatedTotals = (currentTotals: IPOSSegmentTotals, source: SourceType, type: SourcePaymentType, value: number, creditCardPercentFee: number): IPOSSegmentTotals => {
    const updatedTotals = { ...currentTotals };
    const sourceTotals = { ...currentTotals[source] };

    // Update the specific payment type value
    sourceTotals[type] = value;

    // Calculate credit card fee if needed
    if (type === SourcePaymentType.CREDIT) {
      sourceTotals.creditCardFee = calculateCreditCardFee(value, creditCardPercentFee);
      sourceTotals.gross = currentTotals[source].cash + value;
      sourceTotals.net = currentTotals[source].cash + value - sourceTotals.creditCardFee;
    } else {
      sourceTotals.gross = currentTotals[source].creditCard + value;
      sourceTotals.net = currentTotals[source].creditCard + value - currentTotals[source].creditCardFee;
    }

    // Update the source totals
    updatedTotals[source] = sourceTotals;

    // Calculate overall gross and net
    updatedTotals.gross = updatedTotals.tip.gross + updatedTotals.autoGratuity.gross;
    updatedTotals.net = updatedTotals.tip.net + updatedTotals.autoGratuity.net;

    return updatedTotals;
  };

  const onTotalsChange = (id: string, jobTitle: string, segmentId: string, source: SourceType, type: SourcePaymentType, value: number, creditCardPercentFee: number) => {
    const payee = integrationDataClone.data.find((data) => (data.employee ? data.employee?.id === id : data.provider.employeeId === id));

    if (payee) {
      const segment = payee.segments.find((seg) => seg.id === segmentId && seg.job.title.trim().toLowerCase() === jobTitle.trim().toLowerCase());

      if (segment) {
        // check has owner rule
        const ownerRule = getOwnerRule(currentCalculatorRules.rules, payee.provider.employeeId);

        // build change log entries
        const changeLogEntries = buildTipDataChangeLogEntries(
          {
            id: payee.provider.employeeId,
            name: payee.employee?.name || payee.provider.name,
            segmentName: segment.name,
            source,
            type,
            value,
            totals: segment.totals,
            ...(!ownerRule && { jobTitle: jobTitle.trim().toLowerCase() }), // Omitted (undefined) when the payee is covered by an owner rule, so the change log entry does not show a job title.
          },
          currentChangeLogEntries
        );

        // Calculate updated totals
        const updatedSegmentTotals = calculateUpdatedTotals(segment.totals, source, type, value, creditCardPercentFee);
        segment.totals = updatedSegmentTotals;

        // Calculate payee totals by summing all segment totals
        const updatedPayeeTotals = payee.segments.reduce(
          (acc, seg) => {
            acc.tip.cash += seg.totals.tip.cash;
            acc.tip.creditCard += seg.totals.tip.creditCard;
            acc.tip.creditCardFee += seg.totals.tip.creditCardFee;
            acc.tip.gross += seg.totals.tip.gross;
            acc.tip.net += seg.totals.tip.net;

            acc.autoGratuity.cash += seg.totals.autoGratuity.cash;
            acc.autoGratuity.creditCard += seg.totals.autoGratuity.creditCard;
            acc.autoGratuity.creditCardFee += seg.totals.autoGratuity.creditCardFee;
            acc.autoGratuity.gross += seg.totals.autoGratuity.gross;
            acc.autoGratuity.net += seg.totals.autoGratuity.net;

            return acc;
          },
          {
            tip: { cash: 0, creditCard: 0, creditCardFee: 0, gross: 0, net: 0 },
            autoGratuity: { cash: 0, creditCard: 0, creditCardFee: 0, gross: 0, net: 0 },
          }
        );

        payee.totals = {
          ...payee.totals,
          tip: updatedPayeeTotals.tip,
          autoGratuity: updatedPayeeTotals.autoGratuity,
          gross: updatedPayeeTotals.tip.gross + updatedPayeeTotals.autoGratuity.gross,
          net: updatedPayeeTotals.tip.net + updatedPayeeTotals.autoGratuity.net,
        };

        // update change log entries
        setCurrentChangeLogEntries(changeLogEntries);

        // update clone
        setIntegrationDataClone(integrationDataClone);
        setPaymentSetClone({ ...paymentSetClone });
      }
    }
  };

  const renderListItemForShareSegments = useMemo(
    () =>
      segments.map((segment, idx) => {
        const segmentEntryItems: IEmployeeListItemProps[] = [];
        const ownerRuleProviderIds = getOwnerRuleProviderIds(currentCalculatorRules.rules);

        // populate segment entry items
        integrationDataClone.data.forEach((data) => {
          const matchSegments = data.segments.filter((seg) => seg.id === segment.id);
          matchSegments.forEach((match) => {
            // Only include employees who have worked jobs with a share policy (for the default pool,
            // only those contributing) or who are covered by an owner rule.
            if (isTipDataSegmentEntryEligible(match, data.provider.employeeId, sharePolicyJobs, ruleEligibleJobTitles)) {
              const overrideTickets = match.tickets.filter((ticket) => !!ticket.override);

              // Determine the title and subtext to display based on whether the employee is a virtual contributor or not.
              const isOwnerRule = ownerRuleProviderIds.has(data.provider.employeeId);
              const displayName = data?.employee?.name || data.provider?.name?.trim() || capitalize(DEFAULT_POOL_UNKNOWN_JOB_TITLE);

              segmentEntryItems.push({
                title: displayName,
                subText: formatTipDataJobTitle(isOwnerRule, displayName, match.job.title),
                totals: match.totals,
                onTotalsChange: (source, type, value) => onTotalsChange(data.employee ? data.employee.id : data.provider.employeeId, match.job.title, segment.id, source, type, value, currentSiteTipCalcPercentFee),
                isVirtual: isVirtualRuleContributor(data, currentCalculatorRules),
                isOwnerRule,
                creditCardFeeEnabled: !!currentSiteTipCalcPercentFee,
                creditCardPercentFee: currentSiteTipCalcPercentFee,
                overrideTickets,
              });
            }
          });
        });

        if (!segmentEntryItems.length) return <Fragment key={`segment-${segment.id}`} />; // Do not build entry if we don't have segment entries.

        // calculate total
        const total = segmentEntryItems.reduce((acc, entryItem) => acc + entryItem.totals.net, 0);

        return (
          <ListItemWrapper key={`segment-${segment.id}`} data-cy="tip-data-list-item">
            <AccordionListItem key={`segment-${segment.id}`} title={segment.name} defaultOpenState={idx === 0 && true} totalAmount={total} listItems={segmentEntryItems} />
          </ListItemWrapper>
        );
      }),
    [segments, integrationDataClone]
  );

  const [showIntegrationDataProcessingModal, hideIntegrationDataProcessingModal] = useModal(
    () => (
      <TipCalculatorProcessingModal
        mode={TipCalculatorProcessingModalMode.RECALCULATE}
        tipCalcRules={currentCalculatorRules}
        onClose={hideIntegrationDataProcessingModal}
        businessDate={paymentDate}
        site={site}
        segments={paymentSetClone.segments}
        posDataResults={integrationDataClone}
        changeLogEntries={currentChangeLogEntries}
        onSuccess={() => {
          // update original with confirmed new data
          dispatch(updateCalculatorPaymentset(paymentSetClone));
          dispatch(setPOSResults(integrationDataClone));
        }}
      />
    ),
    [currentCalculatorRules, paymentDate, site, paymentSetClone, integrationDataClone]
  );

  const [showAbandonChangesModal, hideAbandonChangesModal] = useModal(
    () => (
      <Alert
        dataCy="abandon-changes-alert"
        title="Abandon Changes?"
        message="You have unsaved changes that haven't been applied to your calculations. Are you sure you want to discard edits?"
        onLeftAction={hideAbandonChangesModal}
        onRightAction={onBack}
        leftActionLabel="Cancel"
        rightActionLabel="Abandon Changes"
        rightActionAppearance="destructive"
        isActive
      />
    ),
    [onBack]
  );

  const onNext = () => {
    showIntegrationDataProcessingModal();
  };

  // Calculate the totals for the summary sidebar
  const calculateSummaryTotals = useMemo((): ITipCalculatorTipDataTotals => calculateTipCalculatorTipDataTotals(integrationDataClone), [integrationDataClone, paymentSetClone]);

  return (
    <LayoutSidebar
      content={
        <>
          {pageHeaderContent}
          <MPContent data-cy="tip-data-list">
            <SectionHeader data-cy="tip-data-header" title="Collected Tips" />
            {renderListItemForShareSegments}
          </MPContent>
        </>
      }
      sidebar={
        <TipCalcTipDataSidebar
          businessDate={dayjs(paymentDate)}
          totals={calculateSummaryTotals}
          creditCardPercentFee={currentSiteTipCalcPercentFee}
          onBack={dirty ? showAbandonChangesModal : onBack}
          onContinue={onNext}
          continueDisabled={!dirty || calculateSummaryTotals.collectedTipsAndAutograts <= 0}
        />
      }
    />
  );
};

export default TipData;
